import { IconDeviceFloppy, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import Regex from '../../constants/Regex';
import {
  IMAGE_ASSET_TYPES,
  REQUIRED_ASSET_ROWS,
  TEXT_ASSET_TYPES,
} from '../../constants/googleAssetTypes';
import useFetch from '../../helpers/useFetch';
import {
  validateAssetAspectRatio,
  withImageSpecLabels,
  withTextLengthLabels,
} from '../../helpers/googleAssetValidation';
import { validateUrlProtocol } from '../../helpers/validateURL';
import { getGoogleAccount } from '../../store/googleSlice';
import ErrorMessage from '../ErrorMessage';

const IMAGE_TYPE_OPTIONS = withImageSpecLabels(IMAGE_ASSET_TYPES);
const TEXT_TYPE_OPTIONS = withTextLengthLabels(TEXT_ASSET_TYPES);

const TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image' },
];

const fieldTypeConfig = (type, fieldType) => {
  const list = type === 'TEXT' ? TEXT_ASSET_TYPES : IMAGE_ASSET_TYPES;
  return list.find((entry) => entry.value === fieldType);
};

const defaultFieldTypeForType = (type) =>
  (type === 'TEXT' ? TEXT_ASSET_TYPES : IMAGE_ASSET_TYPES)[0].value;

const makeRow = (type, fieldType, { locked = false } = {}) => ({
  key: `${type}-${fieldType}-${crypto.randomUUID()}`,
  type,
  fieldType: fieldType || defaultFieldTypeForType(type),
  value: '',
  locked,
});

const buildInitialRows = (requiredMode, defaultType) => {
  if (requiredMode) {
    return REQUIRED_ASSET_ROWS.map(({ type, fieldType }) => makeRow(type, fieldType, { locked: true }));
  }
  const type = defaultType || 'TEXT';
  return [makeRow(type, defaultFieldTypeForType(type))];
};

const validateRow = (row, dimensions) => {
  if (!row.value || !row.value.trim()) {
    return 'Value is required';
  }
  if (row.type === 'TEXT') {
    const cfg = fieldTypeConfig('TEXT', row.fieldType);
    if (cfg && row.value.length > cfg.maxLength) {
      return `Max ${cfg.maxLength} characters for ${cfg.label}`;
    }
    return null;
  }
  const isMergeTag = Regex.mergeTagsRegex.test(row.value);
  if (!isMergeTag && !validateUrlProtocol(row.value)) {
    return 'Invalid URL. Please check the address for typing errors or missing protocol.';
  }
  if (dimensions && dimensions.url === row.value?.trim() && dimensions.width && dimensions.height) {
    const validation = validateAssetAspectRatio(
      { type: 'IMAGE', width: dimensions.width, height: dimensions.height },
      row.fieldType
    );
    if (validation && validation.valid === false) {
      const cfg = fieldTypeConfig('IMAGE', row.fieldType);
      const label = cfg?.label || 'This category';
      if (validation.wrongRatio) {
        return `Image is ${dimensions.width}×${dimensions.height} (${validation.actualRatioLabel}). ${label} needs ${validation.expectedLabel} (${validation.recommended}). Pick a different category or a different image.`;
      }
      if (validation.tooSmall) {
        return `Image is ${dimensions.width}×${dimensions.height} — below the ${validation.minWidth}×${validation.minHeight} minimum for ${label} (recommended ${validation.recommended}).`;
      }
    }
  }
  return null;
};

const GoogleAddAssetsModal = ({ isOpen, close, onSuccess, requiredMode = false, defaultType }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const dispatch = useDispatch();
  const [rows, setRows] = useState(() => buildInitialRows(requiredMode, defaultType));
  const [error, setError] = useState();
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({});
  const probeRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    rows.forEach((row) => {
      if (row.type !== 'IMAGE') return;
      const url = row.value?.trim();
      if (!url || !validateUrlProtocol(url)) return;
      if (probeRef.current[row.key] === url) return;
      probeRef.current[row.key] = url;

      const img = new window.Image();
      img.onload = () => {
        if (cancelled) return;
        setImageDimensions((prev) => ({
          ...prev,
          [row.key]: { url, width: img.naturalWidth, height: img.naturalHeight },
        }));
      };
      img.onerror = () => {
        if (cancelled) return;
        setImageDimensions((prev) => ({ ...prev, [row.key]: { url, error: true } }));
      };
      img.src = url;
    });
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const rowErrors = useMemo(
    () => rows.map((row) => validateRow(row, imageDimensions[row.key])),
    [rows, imageDimensions]
  );
  const hasErrors = rowErrors.some(Boolean);

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        if (patch.type && patch.type !== row.type) {
          next.fieldType = defaultFieldTypeForType(patch.type);
          next.value = '';
        }
        return next;
      })
    );
  };

  const addRow = () => {
    const type = defaultType || 'TEXT';
    setRows((prev) => [...prev, makeRow(type, defaultFieldTypeForType(type))]);
  };

  const removeRow = (index) => {
    const removed = rows[index];
    if (removed) {
      delete probeRef.current[removed.key];
      setImageDimensions((prev) => {
        const next = { ...prev };
        delete next[removed.key];
        return next;
      });
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const resetAndClose = () => {
    setRows(buildInitialRows(requiredMode, defaultType));
    setError(undefined);
    setSubmitted(false);
    setImageDimensions({});
    probeRef.current = {};
    close();
  };

  const save = (e) => {
    e.preventDefault();
    setError(undefined);
    setSubmitted(true);
    if (hasErrors) return;

    const payload = {
      assets: rows.map(({ type, fieldType, value }) =>
        type === 'TEXT'
          ? { type, fieldType, text: value }
          : { type, fieldType, url: value }
      ),
    };

    setSaving(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/google/assets`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((response) => {
        dispatch(getGoogleAccount({ accountId, domainId }));
        if (onSuccess) onSuccess(response.assets);
        resetAndClose();
      })
      .catch(setError)
      .finally(() => setSaving(false));
  };

  const title = requiredMode ? 'Add Required Assets*' : 'Add Asset to Library';

  return (
    <Modal size="xl" opened={isOpen} onClose={resetAndClose} title={title}>
      <form onSubmit={save}>
        <Stack>
          {rows.map((row, index) => {
            const fieldTypeOptions = row.type === 'TEXT' ? TEXT_TYPE_OPTIONS : IMAGE_TYPE_OPTIONS;
            const hasValue = Boolean(row.value?.trim());
            const rowError = submitted || hasValue ? rowErrors[index] : null;
            const imageCfg = row.type === 'IMAGE' ? fieldTypeConfig('IMAGE', row.fieldType) : null;
            const textCfg = row.type === 'TEXT' ? fieldTypeConfig('TEXT', row.fieldType) : null;
            let description = null;
            if (imageCfg?.aspectRatioLabel) {
              const minPart =
                imageCfg.minWidth && imageCfg.minHeight
                  ? ` · Min: ${imageCfg.minWidth}×${imageCfg.minHeight}`
                  : '';
              description = `Required: ${imageCfg.aspectRatioLabel} aspect ratio${minPart} · Recommended: ${imageCfg.recommendedDimensions}`;
            } else if (textCfg?.maxLength) {
              const len = row.value?.length ?? 0;
              description = `Max ${textCfg.maxLength} chars · ${len}/${textCfg.maxLength} used`;
            }
            const widerCategory = 280;
            return (
              <Group key={row.key} align="flex-start" wrap="nowrap">
                <Select
                  label="Type"
                  data={TYPE_OPTIONS}
                  value={row.type}
                  onChange={(value) => updateRow(index, { type: value })}
                  disabled={row.locked || !!defaultType}
                  w={120}
                />
                <Select
                  label="Category"
                  data={fieldTypeOptions}
                  value={row.fieldType}
                  onChange={(value) => updateRow(index, { fieldType: value })}
                  disabled={row.locked}
                  w={widerCategory}
                />
                <TextInput
                  label={row.type === 'TEXT' ? 'Text' : 'Image URL'}
                  description={description}
                  placeholder={row.type === 'TEXT' ? 'Enter text' : 'https://…'}
                  value={row.value}
                  error={rowError}
                  onChange={(e) => updateRow(index, { value: e.target.value })}
                  style={{ flex: 1 }}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  mt={24}
                  onClick={() => removeRow(index)}
                  disabled={row.locked || rows.length === 1}
                  aria-label="Remove row"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            );
          })}
        </Stack>

        <Group mt="md">
          <Button variant="light" onClick={addRow} leftSection={<IconPlus size={16} />}>
            Add another
          </Button>
          {requiredMode && (
            <Text size="xs" c="dimmed">
              Required rows are locked. You may add optional assets below.
            </Text>
          )}
        </Group>

        {error && <ErrorMessage message={error.message} />}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={resetAndClose}>
            Close
          </Button>
          <Button type="submit" disabled={saving} leftSection={<IconDeviceFloppy size={16} />}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default GoogleAddAssetsModal;
